/**
 * Builds the journey outcome webhook's { admiralQuestionId: answer } map from
 * the CRO post-submission questions and every step response's answers. Step
 * answers are merged last, so a server answer wins over a CRO answer on the same
 * Admiral id.
 *
 * @param {object} params
 * @param {string[]} params.componentIds - the post-submission question component ids.
 * @param {object} params.components - the form components store.
 * @param {object} params.stepResponses - stepResponses[stepKey][operation] = { data, answers }.
 * @returns {object} the merged { admiralQuestionId: answer } map.
 */
// eslint-disable-next-line import/prefer-default-export
export const buildPostSubmissionAnswers = ({
  componentIds,
  components,
  stepResponses,
}) => {
  const croAnswers = (componentIds ?? []).reduce((acc, id) => {
    const component = components?.[`component_${id}`];
    const rawMappedQuestionId = component?.component?.mappedQuestionId;
    const answer = component?.input?.value;

    if (typeof rawMappedQuestionId !== 'string' || typeof answer !== 'string') {
      return acc;
    }

    const mappedQuestionId = rawMappedQuestionId.trim();

    if (!mappedQuestionId || !answer.trim()) {
      return acc;
    }

    return { ...acc, [mappedQuestionId]: answer };
  }, {});

  const isBlank = (answer) =>
    answer === undefined || answer === null || answer === '';

  const stepAnswers = Object.values(stepResponses ?? {})
    .flatMap((operations) => Object.values(operations ?? {}))
    .flatMap((response) => Object.entries(response?.answers ?? {}))
    .filter(([, answer]) => !isBlank(answer))
    .reduce((acc, [id, answer]) => ({ ...acc, [id]: answer }), {});

  return { ...croAnswers, ...stepAnswers };
};
